import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { OpenTab } from '../../types/files';
import { getFileIcon } from '../../utils/fileIcons';
import { useCollab } from '../../contexts/CollabContext';
import './EditorTabs.css';

interface EditorTabsProps {
  tabs: OpenTab[];
  previewTab: OpenTab | null;
  activeFile: OpenTab | null;
  onTabClick: (tab: OpenTab) => void;
  onTabClose: (tab: OpenTab) => void;
  onMakePermanent: (tab: OpenTab) => void;
  onTabsReorder?: (newTabs: OpenTab[]) => void;
}

interface SortableTabProps {
  tab: OpenTab;
  isActive: boolean;
  isDragging: boolean;
  onTabClick: (tab: OpenTab) => void;
  onTabClose: (tab: OpenTab, e: React.MouseEvent) => void;
}

const SortableTab: React.FC<SortableTabProps> = ({
  tab,
  isActive,
  isDragging,
  onTabClick,
  onTabClose,
}) => {
  const collab = useCollab();
  const users = collab.openDocuments[tab.file.path]?.users || [];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: tab.file.path });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, 0, 0)` : undefined,
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`editor-tab ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
      onClick={() => onTabClick(tab)}
      title={tab.file.path}
    >
      <img
        src={getFileIcon(tab.file.name, false)}
        alt={tab.file.name}
        className="editor-tab-icon"
      />
      <span className="editor-tab-name">
        {tab.file.name}
      </span>
      {tab.isDirty && <span className="editor-tab-dirty">●</span>}
      
      <span
        className="editor-tab-close"
        onClick={e => onTabClose(tab, e)}
        title="Close"
      >
        ×
      </span>
    </div>
  );
};

interface RegularTabProps {
  tab: OpenTab;
  isActive: boolean;
  isPreview: boolean;
  onTabClick: (tab: OpenTab) => void;
  onTabClose: (tab: OpenTab, e: React.MouseEvent) => void;
  onMakePermanent: (tab: OpenTab) => void;
}

const RegularTab: React.FC<RegularTabProps> = ({
  tab,
  isActive,
  isPreview,
  onTabClick,
  onTabClose,
  onMakePermanent,
}) => {
  return (
    <div
      className={`editor-tab ${isActive ? 'active' : ''} ${isPreview ? 'preview' : ''}`}
      onClick={() => onTabClick(tab)}
      onDoubleClick={() => isPreview && onMakePermanent(tab)}
      title={tab.file.path}
    >
      <img
        src={getFileIcon(tab.file.name, false)}
        alt={tab.file.name}
        className="editor-tab-icon"
      />
      <span className="editor-tab-name">
        {tab.file.name}
      </span>
      {tab.isDirty && <span className="editor-tab-dirty">●</span>}
      
      <span
        className="editor-tab-close"
        onClick={e => onTabClose(tab, e)}
        title="Close"
      >
        ×
      </span>
    </div>
  );
};

const EditorTabs: React.FC<EditorTabsProps> = ({
  tabs,
  previewTab,
  activeFile,
  onTabClick,
  onTabClose,
  onMakePermanent,
  onTabsReorder,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = tabs.findIndex(tab => tab.file.path === active.id);
    const newIndex = tabs.findIndex(tab => tab.file.path === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newTabs = arrayMove(tabs, oldIndex, newIndex);
      onTabsReorder?.(newTabs);
    }
  };

  const handleTabClose = (tab: OpenTab, e: React.MouseEvent) => {
    e.stopPropagation();
    onTabClose(tab);
  };

  return (
    <div className="editor-tabs-container">
      {/* Sol taraf: Permanent tabs (drag & drop ile) */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tabs.map(tab => tab.file.path)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="editor-tabs-left">
            {tabs.map((tab) => {
              const isActive = activeFile?.file.path === tab.file.path;
              const isDragging = activeId === tab.file.path;

              return (
                <SortableTab
                  key={tab.file.path}
                  tab={tab}
                  isActive={isActive}
                  isDragging={isDragging}
                  onTabClick={onTabClick}
                  onTabClose={handleTabClose}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* Sağ taraf: Preview tab (drag & drop yok) */}
      {/* Preview tab sadece permanent tab'larda yoksa göster */}
      {previewTab && !tabs.some(tab => tab.file.path === previewTab.file.path) && (
        <div className="editor-tabs-right">
          <RegularTab
            tab={previewTab}
            isActive={activeFile?.file.path === previewTab.file.path}
            isPreview={true}
            onTabClick={onTabClick}
            onTabClose={handleTabClose}
            onMakePermanent={onMakePermanent}
          />
        </div>
      )}
    </div>
  );
};

export default EditorTabs; 